import { FIRM_CONTACT_LINE, FIRM_NAME, FIRM_PHONE } from './firm';

export type DeliveryChannel = 'email' | 'sms';
export type DeliveryLanguage = 'en' | 'ru' | 'both';
export type DeliveryMessageType = 'invitation' | 'consultation_reminder';

export interface ClientMessageInput {
	type: DeliveryMessageType;
	channel: DeliveryChannel;
	language: DeliveryLanguage;
	clientName: string;
	consultationAt: string | null;
	privateUrl?: string;
}

export interface ClientMessage {
	subject: string;
	body: string;
}

export function renderClientMessage(input: ClientMessageInput): ClientMessage {
	const consultation = input.consultationAt
		? new Date(input.consultationAt).toLocaleString('en-US', {
				timeZone: 'America/New_York',
				dateStyle: 'long',
				timeStyle: 'short',
			})
		: 'your scheduled consultation';
	const english = input.channel === 'email' ? englishEmail(input, consultation) : englishSms(input, consultation);
	const russianConsultation = input.consultationAt
		? new Date(input.consultationAt).toLocaleString('ru-RU', { timeZone: 'America/New_York', dateStyle: 'long', timeStyle: 'short' })
		: 'назначенное время консультации';
	const russian = input.channel === 'email' ? russianEmail(input, russianConsultation) : russianSms(input, russianConsultation);
	return {
		subject: input.type === 'invitation' ? `Your ${FIRM_NAME} estate planning questionnaire` : `${FIRM_NAME} consultation reminder`,
		body: input.language === 'en' ? english : input.language === 'ru' ? russian : `${english}\n\n${russian}`,
	};
}

function englishEmail(input: ClientMessageInput, consultation: string): string {
	if (input.type === 'invitation') {
		return `Hello ${input.clientName},

${FIRM_NAME} has prepared your estate planning questionnaire. Open the private link below to complete it before your Initial Consultation. You may save your answers and return using the same link.
${input.consultationAt ? `\nYour Initial Consultation is scheduled for ${consultation} (Eastern Time).\n` : ''}

${input.privateUrl ?? ''}

Please keep this link confidential. If you have questions or need help, contact the firm.

${FIRM_CONTACT_LINE}`;
	}
	return `Hello ${input.clientName},

This is a reminder that your Initial Consultation with ${FIRM_NAME} is scheduled for ${consultation} (Eastern Time). If your questionnaire is not complete, please return to it using the private link previously sent by the firm.

If you have questions or need to reschedule, contact the firm.

${FIRM_CONTACT_LINE}`;
}

function englishSms(input: ClientMessageInput, consultation: string): string {
	if (input.type === 'invitation') {
		return `${FIRM_NAME}: Hello ${input.clientName}. Complete your estate planning questionnaire using this private link: ${input.privateUrl ?? ''} Keep the link confidential. Questions: ${FIRM_PHONE}. Reply STOP to unsubscribe or HELP for help. Msg & data rates may apply.`;
	}
	return `${FIRM_NAME}: Reminder for ${input.clientName}: your Initial Consultation is scheduled for ${consultation}. Use the private questionnaire link previously sent by the firm. Questions or changes: ${FIRM_PHONE}. Reply STOP to unsubscribe or HELP for help. Msg & data rates may apply.`;
}

function russianEmail(input: ClientMessageInput, consultation: string): string {
	if (input.type === 'invitation') {
		return `Здравствуйте, ${input.clientName}.

${FIRM_NAME} подготовила для вас анкету по наследственному планированию. Откройте личную ссылку ниже и заполните анкету до первоначальной консультации. Вы можете сохранить ответы и вернуться к анкете по той же ссылке.
${input.consultationAt ? `\nВаша первоначальная консультация назначена на ${consultation} (восточное время США, Нью-Йорк).\n` : ''}

${input.privateUrl ?? ''}

Эта ссылка конфиденциальна. Если у вас есть вопросы или вам нужна помощь, свяжитесь с фирмой.

${FIRM_CONTACT_LINE}`;
	}
	return `Здравствуйте, ${input.clientName}.

Напоминаем, что ваша первоначальная консультация с ${FIRM_NAME} назначена на ${consultation} (восточное время США, Нью-Йорк). Если анкета еще не заполнена, вернитесь к ней по личной ссылке, которую фирма отправила ранее.

Если у вас есть вопросы или нужно перенести консультацию, свяжитесь с фирмой.

${FIRM_CONTACT_LINE}`;
}

function russianSms(input: ClientMessageInput, consultation: string): string {
	if (input.type === 'invitation') {
		return `${FIRM_NAME}: Здравствуйте, ${input.clientName}. Заполните анкету по наследственному планированию по личной ссылке: ${input.privateUrl ?? ''} Храните ссылку в тайне. Вопросы: ${FIRM_PHONE}. Для отказа ответьте STOP, для помощи — HELP. Может взиматься плата за сообщения и передачу данных.`;
	}
	return `${FIRM_NAME}: Напоминание для ${input.clientName}: первоначальная консультация назначена на ${consultation}. Используйте личную ссылку на анкету, отправленную ранее. Вопросы: ${FIRM_PHONE}. Для отказа ответьте STOP, для помощи — HELP. Может взиматься плата за сообщения и передачу данных.`;
}
